import Phaser from 'phaser';
import { SCENE, assetUrl } from '../config.js';
import { ASSETS, NEIGHBOURS, audioId, needsFor, tiers } from '../assetManifest.mjs';
import { leanTiers, loginTiers, evictable, crowdTiers, crowdedNow } from '../textureBudget.mjs';
import { PIXELS } from '../assetPixels.mjs';
import { MUSIC_KEY } from '../audio/eventMap.js';
import { isTouchDevice } from '../ui/isTouchDevice.js';

// Phones keep only what the current scene and its neighbours need, and fetch on demand
// what a sprite waits for (textureBudget.mjs).
const LEAN = isTouchDevice() && new URLSearchParams(globalThis.location?.search ?? '').get('lean') !== '0';
import { PreloadQueue, BATCH_SIZE } from '../preloadQueue.mjs';
import { installMissingTextureHealer, healMissingTextures } from '../ui/missingTextureHealer.js';
import { HAIR_SHEETS, IDLE_MS } from '../look/hairSheets.mjs';
import { freeHairSheets } from '../ui/walkAnim.js';

// The invisible background loader (staged-loading design, decision C). Started once by
// BootScene next to the game, never stopped: it drains `tiers(scene, ctx)` into the shared
// texture/audio caches, one tier per loader run, so `prioritise()` (login, a scene change)
// only reorders what has not started. Scenes read the caches as they always did; the few
// places that draw OTHER players' things use `whenReady` to swap a texture in late.
export class PreloadScene extends Phaser.Scene {
  constructor() { super(SCENE.PRELOAD); }

  create() {
    installMissingTextureHealer();   // sprites drawn before their art landed get it later
    this.queue = new PreloadQueue((k) => this.isReady(k) || claimed.has(k));
    this._waiting = new Map();   // key -> [callbacks]
    this._running = false;
    this._aborted = new Set();   // cache keys of the current batch we cancelled in yield()
    this._failed = new Set();    // cache keys of the current batch whose download failed
    this._attempts = new Map();  // id -> how many batches it has failed in (retry backoff)
    // Never more than one batch's worth of connections: on a slow link (prod measured at
    // ~250 KB/s) 16 parallel background files starved the login API calls and every
    // scene's own blocking load — the screen stayed black for a minute.
    this.load.maxParallelDownloads = BATCH_SIZE;
    // Phones also free the hair sheets nobody draws between scene changes: the Wygląd panel's tiles load a page of
    // styles at once, and a long stay in one scene meets many players' styles (final review).
    if (LEAN) this.time.addEvent({ delay: IDLE_MS, loop: true, callback: () => this._freeHair(this._inUse()) });
    preloader._scene = this;
    // Drain calls made before this scene existed (see the module-level queue below).
    for (const [key, cb] of pendingWhenReady.splice(0)) this.whenReady(key, cb);
    if (pendingPrioritise) { const [sceneKey, ctx] = pendingPrioritise; pendingPrioritise = null; this.prioritise(sceneKey, ctx); }
    this._pump();
  }

  isReady(id) {
    const a = ASSETS[id];
    if (!a) return false;
    return a.kind === 'audio' ? this.cache.audio.exists(a.key) : this.textures.exists(a.key);
  }

  prioritise(sceneKey, ctx) {
    this.sweep(); // cheap; catches ids another loader (a scene's own preload()) finished meanwhile
    this._last = [sceneKey, ctx];
    let t = tiers(sceneKey, ctx, { lean: LEAN });
    if (LEAN) {
      t = ctx.login ? loginTiers(t, audioId(MUSIC_KEY)) : leanTiers(t, PIXELS);
      const home = NEIGHBOURS[sceneKey]?.[0];
      if (this._crowded) t = crowdTiers(t, new Set(home ? needsFor(home, ctx) : []));
      const keep = new Set([...t[0], ...t[1]].map((id) => ASSETS[id]?.key).filter(Boolean));
      this.time.delayedCall(400, () => this._freeUnneeded(keep));   // after the old scene's objects are gone
    }
    // What sprites still wait for stays first in line: no sweep brings it (phones skip most of it, and every device
    // skips other styles' car pictures), and setTiers drops what whenReady pushed.
    const waited = [...this._waiting.keys()].filter((id) => ASSETS[id] && !this.isReady(id));
    if (waited.length) t = [waited, ...t];
    this.queue.setTiers(t);
    if (import.meta.env?.DEV) console.info(`[Preload] ${sceneKey}: tiers ${t.map((x) => x.length).join('/')} keys, ${this.queue.pending} pending`);
    this._pump();
  }

  // How many other players the scene on screen has (remotePresence). A phone re-plans its tiers when the scene turns
  // crowded or back (textureBudget CROWD, Gate M option B): crowded, the neighbours' backgrounds but the way home's
  // are dropped and freed; back, they are fetched again.
  setCrowd(others) {
    const now = LEAN && crowdedNow(this._crowded, others);
    if (now === !!this._crowded) return;
    this._crowded = now;
    if (this._last) this.prioritise(...this._last);
  }

  // Phones: drop the big textures that neither the scene on screen nor its neighbours use
  // (textureBudget.mjs), and the walk hair sheets no sprite has drawn for a while (look/hairSheets.mjs). Anything
  // still drawn anywhere is kept, whatever the tiers say.
  _freeUnneeded(keep) {
    const inUse = this._inUse();
    const loaded = this.textures.getTextureKeys().map((key) => {
      const src = this.textures.get(key).source ?? [];
      return { key, px: src.reduce((a, x) => a + (x.isCanvas ? 0 : x.width * x.height), 0) };
    });
    const out = evictable({ loaded, keep, inUse });
    for (const key of out) this.textures.remove(key);
    if (out.length && import.meta.env?.DEV) console.info(`[Preload] freed ${out.length} big textures`, out);
    this._freeHair(inUse);
  }

  // The textures something on screen draws, in any scene.
  _inUse() {
    const inUse = new Set();
    const walk = (list) => { for (const o of list) { if (o.texture?.key) inUse.add(o.texture.key); if (o.list) walk(o.list); } };
    for (const sc of this.game.scene.scenes) if (sc.sys.displayList) walk(sc.sys.displayList.list);
    return inUse;
  }

  _freeHair(inUse) {
    const hair = HAIR_SHEETS.unused(this.textures.getTextureKeys()).filter((k) => !inUse.has(k.replace(/_mask$/, '')));
    freeHairSheets(this, hair);
    if (hair.length && import.meta.env?.DEV) console.info(`[Preload] freed ${hair.length} hair sheets`, hair);
  }

  _scheduleRetry(ids) {
    const again = ids.filter((id) => {
      const n = (this._attempts.get(id) ?? 0) + 1;
      this._attempts.set(id, n);
      return n <= RETRY_DELAYS_MS.length;
    });
    if (!again.length) return;
    const delay = RETRY_DELAYS_MS[Math.max(...again.map((id) => this._attempts.get(id))) - 1];
    this.time.delayedCall(delay, () => { this.queue.pushFront(again); this._pump(); });
  }

  /** A scene's own blocking download of these failed: fetch them first, then whenReady swaps them in. */
  retry(ids) {
    this.queue.pushFront(ids);
    this._pump();
  }

  // Fires `cb` once `id` is cached — by this loader, or by any other loader (a scene's own
  // preload()) — whichever notices first via `sweep()`. Never fires if the file is missing.
  whenReady(key, cb) {
    if (this.isReady(key)) { cb(); return; }
    const list = this._waiting.get(key) ?? [];
    list.push(cb);
    this._waiting.set(key, list);
    // Whatever is waited for (another player's clothes, a car model, their style's car picture) is fetched now:
    // phones skip the big part of the sweep and every device skips other styles' car pictures. Once per key, the
    // first waiter asks.
    if (ASSETS[key] && list.length === 1) { this.queue.pushFront([key]); this._pump(); }
  }

  // Resolves any waiter whose id is now in the cache, however it got there (this loader,
  // or another scene's own preload()). Called after every batch and at prioritise() time;
  // the scene-side loading helper (Task 6+) also calls it after its own loader completes.
  sweep() {
    healMissingTextures(this.textures);
    for (const id of [...this._waiting.keys()]) {
      if (this.isReady(id)) {
        const cbs = this._waiting.get(id);
        this._waiting.delete(id);
        // A waiter can belong to a GameObject that's since been destroyed (a peer left, a
        // scene shut down) — never let one throwing callback take the loader down with it.
        cbs.forEach((cb) => { try { cb(); } catch (e) { console.warn('[Preload] whenReady callback failed', id, e); } });
      }
    }
  }

  // Cancel the in-flight batch so a blocking load (a scene's preload(), BootScene's API
  // calls) gets the whole link. Phaser has no abort API: abort the XHR ourselves, then run
  // the file's own error path with retries off so the loader's bookkeeping completes; the
  // keys go back to the head of the queue in the `complete` handler.
  yield() {
    if (!this._running) return;
    const aborted = [];
    for (const file of [...this.load.inflight.entries]) {
      const xhr = file.xhrLoader;
      if (!xhr || xhr.readyState === 4) continue;
      xhr.abort();
      file.retryAttempts = 0;
      this._aborted.add(file.key);
      aborted.push(file);
    }
    for (const file of aborted) file.onError();
  }

  _pump() {
    // A scene is loading its own tier 0 (claims) or BootScene is talking to the API (hold):
    // stay out of the way; release()/unhold() pump again.
    if (this._running || holds > 0 || claimed.size > 0) return;
    const batch = this.queue.nextBatch();
    if (!batch.length) return;
    this._running = true;
    for (const id of batch) {
      const a = ASSETS[id];
      if (a.kind === 'audio') this.load.audio(a.key, assetUrl(`/assets/${a.path}`));
      else this.load.image(a.key, assetUrl(`/assets/${a.path}`));
    }
    // Phaser emits filecomplete with the cache key; a sound's id is `audio:<key>`.
    const onFileComplete = (key, type) => {
      const id = type === 'audio' ? audioId(key) : key;
      warnIfOverTextureLimit(this, key, type);
      const cbs = this._waiting.get(id);
      if (cbs) {
        this._waiting.delete(id);
        cbs.forEach((cb) => { try { cb(); } catch (e) { console.warn('[Preload] whenReady callback failed', id, e); } });
      }
    };
    // `on`, not `once`: a batch can have several missing files and every one should warn
    // (the loader keeps going past a single 404), so this listener must survive past the
    // first `loaderror` — it's removed alongside `filecomplete` in the `complete` handler.
    const onLoadError = (file) => { if (!this._aborted.has(file.key)) { this._failed.add(file.key); console.warn(`[Preload] missing ${file.key}`); } };
    this.load.on('filecomplete', onFileComplete);
    this.load.on('loaderror', onLoadError);
    this.load.once('complete', () => {
      this.load.off('filecomplete', onFileComplete);
      this.load.off('loaderror', onLoadError);
      // Keys we cancelled in yield() are not done: they go back to the head of the queue.
      const abandoned = batch.filter((id) => this._aborted.has(ASSETS[id].key));
      this._aborted.clear();
      this.queue.abandon(abandoned);
      this.queue.markDone(batch);
      // A download that failed outright (a deploy restarting the server, a flaky link) is
      // tried again a few times with growing pauses — while it is missing, whatever was
      // drawn with it shows Phaser's green box, and the healer fixes that when it lands.
      const failed = batch.filter((id) => this._failed.has(ASSETS[id].key));
      this._failed.clear();
      this._scheduleRetry(failed);
      // Reset before sweep(): a throwing waiter is now caught (see sweep()), but resetting
      // first means even an unexpected throw elsewhere in this handler can't wedge the pump.
      this._running = false;
      this.sweep();
      // Anything from this batch still waiting failed to load (404 etc); drop it without
      // firing rather than leaving the callback (and its closure) parked forever.
      const batchIds = new Set(batch);
      for (const id of this._waiting.keys()) {
        if (batchIds.has(id)) this._waiting.delete(id);
      }
      this._pump();
    });
    this.load.start();
  }
}

// A texture wider/taller than the GPU allows uploads as nothing and draws black (the 8358-px
// street did, on phones — it is tiled now). Cheap to check on every image that lands.
function warnIfOverTextureLimit(scene, key, type) {
  if (type !== 'image') return;
  const gl = scene.game.renderer?.gl;
  if (!gl) return;
  const max = scene._maxTextureSize ??= gl.getParameter(gl.MAX_TEXTURE_SIZE);
  const src = scene.textures.get(key)?.source?.[0];
  if (src && (src.width > max || src.height > max)) {
    console.warn(`[Preload] ${key} is ${src.width}×${src.height} — over this GPU's ${max}-px texture limit, it will draw black`);
  }
}

// Pauses before re-trying a background download that failed: enough to ride out a server
// restart (a deploy takes ~10–20 s) without hammering it.
const RETRY_DELAYS_MS = [2000, 5000, 10000, 20000];

// Calls made before PreloadScene.create() has run (its own preload() or another scene's
// create() can race it — Phaser doesn't run a just-launched scene's create() until the next
// step). Queued here and drained by create() above, in order, through the real methods.
let pendingWhenReady = [];
let pendingPrioritise = null; // only the latest matters: it's what the real prioritise() would leave queued

// Ids some other loader is fetching right now — module level, because a scene's preload()
// can claim before PreloadScene.create() has ever run. Treated as "already loaded" by the
// queue so the background loader skips them instead of downloading them a second time.
const claimed = new Set();
const addClaims = (ids) => { for (const id of ids) claimed.add(id); };
const dropClaims = (ids) => { for (const id of ids) claimed.delete(id); };
// hold()/unhold() counter: BootScene holds the background loader while it talks to the API.
let holds = 0;

// The handle everything else uses; `_scene` is set once the scene is created.
export const preloader = {
  _scene: null,
  prioritise(sceneKey, ctx) { if (this._scene) this._scene.prioritise(sceneKey, ctx); else pendingPrioritise = [sceneKey, ctx]; },
  whenReady(key, cb) { if (this._scene) this._scene.whenReady(key, cb); else pendingWhenReady.push([key, cb]); },
  isReady(key) { return this._scene?.isReady(key) ?? false; },
  setCrowd(n) { this._scene?.setCrowd(n); },
  sweep() { this._scene?.sweep(); },
  // claim/release are NOT routed through `_scene`: a scene's preload() can run before
  // PreloadScene.create() ever has, and a claim that silently no-ops there is exactly the
  // double download this exists to prevent. The set is module state; the queue reads it.
  // A claim also cancels whatever the background loader has in flight, and nothing new
  // starts until every claim is released — the blocking load owns the link meanwhile.
  claim(ids) { addClaims(ids); this._scene?.yield(); },
  release(ids) { dropClaims(ids); this._scene?._pump(); },
  retry(ids) { if (this._scene) this._scene.retry(ids); },
  hold() { holds++; this._scene?.yield(); },
  unhold() { holds = Math.max(0, holds - 1); this._scene?._pump(); },
};
